// 261010 Epic 2 / P2b-1 + P2b-2 + P2b-3 — CHÍNH SÁCH THAO TÁC của khung mới (thuần: không antd/router, jest chạy được).
// Quyết HIỂN THỊ / BẬT / XÁC NHẬN cho các thao tác ở bước Sơ đồ, Storyboard, Prompt (P2b-1), Video, Ghép (P2b-2) và Kịch bản, Đạo diễn (P2b-3: sửa tay). Server vẫn là nơi chặn thật (khoá việc, hash, baseVersion) — RIÊNG `batch-edit` KHÔNG kiểm job / khoá / Sky nên ở P2b-3 lớp FE này là lớp chặn DUY NHẤT.
// Bảng key/luật/xác nhận: `project-docs/261010-hoc-luong-sky/261010-epic2-phase2b1.md` §1. KHÔNG hiện giá ở bất cứ chỗ nào (User chốt).
import type { FilmEvent, FilmExportStatus, FilmRunningJob, GenTuning, PositionMapsResponse, Storyboard } from '../../types/film';
import type { WorkflowStepKey, WorkflowStepState } from '../../types/filmWorkflow';
import { apiErrorText } from './apiErrorText';
import { optInTuningOf } from './workflowGenerationUtils';

export type ConfirmKind = 'NONE' | 'GEN_IMAGE' | 'OVERWRITE_EDITED' | 'DELETE' | 'GEN_VIDEO' | 'GEN_ASSEMBLY';
export type ApprovalStateLite = 'NONE' | 'APPROVED' | 'OUTDATED';

/** Hợp đồng ctx (Phase doc §1). Khác doc: setup có thêm `stale`/`busy` (cần cho luật "ảnh cũ" và "setup bận"); `staleness` là chuỗi thật của BE (`FRESH | INPUT_CHANGED | NO_SHEET`). */
export interface WorkflowActionCtx {
  isSky: boolean;
  stepState: WorkflowStepState;
  runningJobKinds: string[];
  map?: {
    setups: Array<{ id: string; hasImage: boolean; approvalState: ApprovalStateLite; isAnchor: boolean; anchorHasImage: boolean; stale: boolean; busy: boolean }>;
    linkedPhases: number; totalPhases: number;
  };
  storyboard?: {
    hasShots: boolean; hasFrames: boolean; isDirty: boolean; hasImage: boolean; staleness: string | null; approvalState: ApprovalStateLite;
    authorStatus: 'AI' | 'EDITED' | 'AGENT' | null;
  };
  prompt?: { hasPrompt: boolean; authorStatus: 'AUTHORED' | 'EDITED' | null; promptModeState: 'OK' | 'MISMATCH' | 'MISSING'; isDirty?: boolean };
  /** 261010 P2b-2 — bước Video của MỘT video. Khác doc: `durationS` có thể `null` (event chưa có giây — không bịa số), thêm `resolution` (hộp xác nhận nêu độ phân giải đã chọn). */
  video?: {
    /** "Seedance 2.0 mini" | "Seedance 2.5" … (từ `ev.videoEngine` + model đã chọn). */
    engineLabel: string;
    resolution?: string;
    durationS: number | null;
    count: number;
    hasClips: boolean;
    selectedClipId: string | null;
    /** `usable` = cùng luật BE `selectedClipUsable` (clip khớp engine/giây của video). */
    clips: Array<{ id: string; status: 'PENDING' | 'DONE' | 'FAILED'; usable: boolean }>;
    promptGate: { ok: boolean; reason?: string };
    promptStale: boolean;
    busy: boolean;
  };
  /** 261010 P2b-2 — bước Ghép (cấp plan). `missing` = video chưa có clip được chọn (để liệt kê + liên kết sang bước Video). */
  assembly?: {
    total: number; withSelected: number; missing: Array<{ eventId: string; eventOrder: number }>;
    exportStatus: FilmExportStatus | null; hasExport: boolean; exportStale: boolean; busy: boolean;
  };
  /**
   * 261010 P2b-3 — bước Kịch bản / Đạo diễn (sửa tay MỘT video). `jobs` = TOÀN BỘ việc nền của plan (không lọc theo video: job ghi plan ở video khác cũng chặn; riêng job gen clip chỉ chặn khi là của CHÍNH video này).
   * `editing` = đang mở trình sửa; `dirty` = nháp khác bản đã tải.
   */
  edit?: { eventId: string; editing: boolean; dirty: boolean; jobs: Array<Pick<FilmRunningJob, 'type' | 'eventId' | 'key'>> };
}

export interface ActionState {
  key: string; label: string; visible: boolean; enabled: boolean; reason?: string; confirm: ConfirmKind;
  /** Thao tác theo từng setup sơ đồ. */
  subjectId?: string;
  /** Body gửi API (PROMPT_REBUILD: `{ force: true }` khi prompt đã sửa tay; VIDEO_*: `variant` — false = clip mới tự được chọn khi xong, true = thêm bản không đổi bản đang chọn). */
  payload?: { force?: boolean; variant?: boolean };
}

export const JOB_KIND = {
  MAP: 'FILM_POSITION_MAP', SETUPS: 'FILM_POSITION_SETUPS', STORYBOARD: 'FILM_STORYBOARD', AUTHOR: 'FILM_AUTHOR_PROMPTS',
} as const;
const RUNNING_TEXT = 'Đang có việc cùng loại chạy ở nền — đợi xong.';

const act = (key: string, label: string, o: Partial<ActionState> & { visible: boolean; enabled: boolean }): ActionState =>
  ({ key, label, confirm: 'NONE', ...o, ...(o.enabled ? {} : { reason: o.reason ?? RUNNING_TEXT }) } as ActionState);

export const actionOf = (list: ActionState[], key: string, subjectId?: string): ActionState | undefined =>
  list.find((a) => a.key === key && (subjectId === undefined || a.subjectId === subjectId));

// ───────────────────────── SƠ ĐỒ ─────────────────────────

function mapActions(ctx: WorkflowActionCtx): ActionState[] {
  const kinds = ctx.runningJobKinds;
  const proposeBusy = kinds.includes(JOB_KIND.SETUPS) || kinds.includes(JOB_KIND.MAP);
  const out: ActionState[] = [
    act('MAP_PROPOSE', 'AI đề xuất setup', { visible: true, enabled: !proposeBusy, reason: 'Việc sơ đồ đang chạy ở nền — đợi xong rồi mới đề xuất.' }),
    act('MAP_OPEN_PANEL', 'Quản lý sơ đồ cả plan', { visible: true, enabled: true }),
  ];
  for (const s of ctx.map?.setups ?? []) {
    const needAnchor = !s.isAnchor && !s.anchorHasImage;
    const genEnabled = !s.busy && !needAnchor;
    out.push(act('MAP_GENERATE', s.hasImage ? 'Tạo lại ảnh' : 'Tạo ảnh', {
      subjectId: s.id, visible: !s.hasImage || s.stale, enabled: genEnabled, confirm: 'GEN_IMAGE',
      reason: s.busy ? 'Setup này đang tạo ảnh ở nền — đợi xong.' : needAnchor ? 'Setup phụ cần ảnh chính của bối cảnh trước — tạo ảnh chính trước.' : undefined,
    }));
    out.push(act('MAP_APPROVE', 'Duyệt', {
      subjectId: s.id, visible: s.hasImage && s.approvalState !== 'APPROVED', enabled: s.hasImage && s.approvalState !== 'APPROVED',
      reason: !s.hasImage ? 'Chưa có ảnh.' : 'Đã duyệt rồi.',
    }));
    out.push(act('MAP_UNAPPROVE', 'Bỏ duyệt', { subjectId: s.id, visible: s.approvalState === 'APPROVED', enabled: true }));
  }
  return out;
}

// ───────────────────────── STORYBOARD ─────────────────────────

function storyboardActions(ctx: WorkflowActionCtx): ActionState[] {
  const sb = ctx.storyboard ?? { hasShots: false, hasFrames: false, isDirty: false, hasImage: false, staleness: null, approvalState: 'NONE' as const, authorStatus: null };
  const busy = ctx.runningJobKinds.includes(JOB_KIND.STORYBOARD);
  const has = sb.hasFrames || sb.hasImage;
  return [
    act('SB_COMPOSE', sb.hasFrames ? 'AI soạn lại' : 'AI soạn', {
      visible: true, enabled: sb.hasShots && !busy,
      reason: !sb.hasShots ? 'Video chưa có shot — dựng camera/shot trước rồi mới soạn khung được.' : 'Đang soạn / tạo storyboard ở nền — đợi xong.',
    }),
    act('SB_SAVE_FRAMES', 'Lưu khung', { visible: sb.isDirty, enabled: sb.isDirty, reason: 'Không có thay đổi để lưu.' }),
    act('SB_GENERATE', sb.hasImage ? 'Tạo lại tấm' : 'Tạo tấm', {
      visible: sb.hasFrames, enabled: sb.hasFrames && !sb.isDirty && !busy, confirm: 'GEN_IMAGE',
      reason: sb.isDirty ? 'Có chỉnh sửa chưa lưu — lưu khung trước khi tạo tấm.' : 'Đang soạn / tạo storyboard ở nền — đợi xong.',
    }),
    act('SB_APPROVE', 'Duyệt', {
      visible: sb.hasImage && sb.approvalState !== 'APPROVED', enabled: sb.hasImage && sb.staleness === 'FRESH' && !busy,
      reason: sb.staleness !== 'FRESH' ? 'Tấm đã cũ so với đầu vào — tạo lại tấm rồi mới duyệt.' : 'Đang soạn / tạo storyboard ở nền — đợi xong.',
    }),
    act('SB_UNAPPROVE', 'Bỏ duyệt', { visible: sb.approvalState === 'APPROVED', enabled: true }),
    act('SB_DELETE', 'Xoá storyboard', { visible: has, enabled: !busy, confirm: 'DELETE', reason: 'Đang soạn / tạo storyboard ở nền — đợi xong.' }),
  ];
}

// ───────────────────────── PROMPT ─────────────────────────

function promptActions(ctx: WorkflowActionCtx): ActionState[] {
  const p = ctx.prompt ?? { hasPrompt: false, authorStatus: null, promptModeState: 'MISSING' as const };
  const busy = ctx.runningJobKinds.includes(JOB_KIND.AUTHOR);
  const edited = p.authorStatus === 'EDITED';
  return [
    act('PROMPT_VIEW_ENGINE', 'Xem bản gửi engine', { visible: true, enabled: p.hasPrompt, reason: 'Chưa có prompt.' }),
    // Ô sửa là chip editor có nút Lưu riêng (không báo "có thay đổi" ra ngoài) ⇒ `isDirty` chỉ tắt khi caller nói rõ là false.
    act('PROMPT_SAVE', 'Lưu prompt', { visible: p.hasPrompt, enabled: p.hasPrompt && !busy && p.isDirty !== false, reason: busy ? 'Đang dựng prompt ở nền — đợi xong.' : 'Không có thay đổi để lưu.' }),
    act('PROMPT_REBUILD', p.hasPrompt ? 'Dựng lại prompt' : 'Dựng prompt', {
      visible: true, enabled: !busy, confirm: edited ? 'OVERWRITE_EDITED' : 'NONE', payload: edited ? { force: true } : {},
      reason: 'Đang dựng prompt ở nền — đợi xong.',
    }),
  ];
}

// ───────────────────────── VIDEO (P2b-2) ─────────────────────────

const VIDEO_BUSY_TEXT = 'Video này đang gen ở nền — đợi xong.';
const NO_VIDEO_DATA: NonNullable<WorkflowActionCtx['video']> = {
  engineLabel: '', durationS: null, count: 1, hasClips: false, selectedClipId: null, clips: [],
  promptGate: { ok: false, reason: 'Chưa tải được dữ liệu video — bấm Tải lại.' }, promptStale: false, busy: false,
};

function videoActions(ctx: WorkflowActionCtx): ActionState[] {
  const v = ctx.video ?? NO_VIDEO_DATA;
  const genReason = v.busy ? VIDEO_BUSY_TEXT : !v.promptGate.ok ? (v.promptGate.reason ?? 'Prompt chưa hợp lệ — dựng lại prompt ở bước 7.') : undefined;
  const canGen = !genReason;
  const out: ActionState[] = [
    act('VIDEO_GEN', 'Tạo clip', { visible: !v.hasClips, enabled: canGen, confirm: 'GEN_VIDEO', payload: { variant: false }, reason: genReason }),
    // Không có "Tạo lại": lượt gen không-variant tự đổi bản đang chọn (BE `settleEventClipDone`). Khi đã có clip chỉ có "Thêm bản" (variant=true: không đổi bản đang chọn; người chọn tay).
    act('VIDEO_VARIANT', 'Thêm bản clip', { visible: v.hasClips, enabled: canGen, confirm: 'GEN_VIDEO', payload: { variant: true }, reason: genReason }),
    act('VIDEO_TUNING', 'Tuỳ chọn gen', { visible: true, enabled: !v.busy, reason: VIDEO_BUSY_TEXT }),
  ];
  for (const c of v.clips) {
    const isSel = c.id === v.selectedClipId;
    out.push(act('VIDEO_SELECT', isSel ? 'Đang chọn' : 'Chọn', {
      subjectId: c.id, visible: c.status === 'DONE', enabled: !isSel && c.usable,
      reason: isSel ? 'Bản này đang được chọn.' : 'Clip gen theo engine/giây khác video này',
    }));
    out.push(act('VIDEO_DELETE', 'Xoá', { subjectId: c.id, visible: true, enabled: !v.busy, confirm: 'DELETE', reason: VIDEO_BUSY_TEXT }));
  }
  return out;
}

// ───────────────────────── GHÉP (P2b-2) ─────────────────────────

function assemblyActions(ctx: WorkflowActionCtx): ActionState[] {
  const a = ctx.assembly ?? { total: 0, withSelected: 0, missing: [], exportStatus: null, hasExport: false, exportStale: false, busy: false };
  const reason = a.busy ? 'Đang dựng ở nền — đợi xong.'
    : a.total === 0 ? 'Plan chưa có video nào để dựng.'
    : a.withSelected < a.total ? `Còn ${a.total - a.withSelected}/${a.total} video chưa chọn clip` : undefined;
  // Không gửi `force`: ở server `force` = bắt AI nghe lại để tạo lại phụ đề (UI cũ chỉ gửi khi người bật công tắc riêng).
  return [
    act('ASM_ASSEMBLE', 'Dựng cả tập', { visible: !a.hasExport, enabled: !reason, confirm: 'GEN_ASSEMBLY', reason }),
    act('ASM_REASSEMBLE', 'Dựng lại', { visible: a.hasExport, enabled: !reason, confirm: 'GEN_ASSEMBLY', reason }),
  ];
}

// ───────────────────────── KỊCH BẢN / ĐẠO DIỄN — sửa tay (P2b-3) ─────────────────────────

/**
 * Việc nền GHI vào plan (event / shot / thoại / cờ cũ). Đang chạy BẤT KỲ việc nào trong danh sách trên plan ⇒ khoá Sửa / Lưu.
 * Tên khớp `FILM_JOB` ở BE (`admin/services/filmJobs.ts`) và `JOB_TYPES_BY_STEP` (`utils/filmWorkflowStatus.ts`; riêng `FILM_WORKFLOW_RUN` không ở bảng đó vì chặng khai ở `phase`, nhưng ghi plan nên vẫn khoá ở đây). `FILM_STORYBOARD` / `FILM_POSITION_MAP` KHÔNG ở đây: drift của chúng đã được phát hiện riêng.
 * Khoá cảnh KHÔNG thay luật này: nó chỉ là cờ bỏ qua khi regen, `batch-edit` không kiểm.
 */
// Cố ý để NGOÀI: `FILM_GEN_PLAN` / `FILM_WRITE_SCRIPTS` / `FILM_HANDOFF_SCRIPTS` khoá theo storyId nên không nằm trong `jobs` của `workflow-status`; `FILM_AGENT_STEP_DRY_RUN` không ghi DB.
export const PLAN_WRITE_JOB_KINDS = [
  'FILM_SCRIPT_PASSES', 'FILM_DIALOGUE', 'FILM_BREAKDOWN', 'FILM_DIRECT', 'FILM_DURATIONS', 'FILM_CONTINUITY', 'FILM_AUTHOR_PROMPTS', 'FILM_GEN_EVENTS', 'FILM_EVENT_STRUCTURE',
  'FILM_WORKFLOW_RUN', // 261010 P2c-1: máy tự chạy chặng 1–2 (BE) ghi thoại / shot / giây của plan
] as const;
const PLAN_WRITE_JOB_LABEL: Record<(typeof PLAN_WRITE_JOB_KINDS)[number], string> = {
  FILM_SCRIPT_PASSES: 'các bước kịch bản', FILM_DIALOGUE: 'viết thoại', FILM_BREAKDOWN: 'chốt location', FILM_DIRECT: 'dựng camera',
  FILM_DURATIONS: 'tính thời lượng', FILM_CONTINUITY: 'soát liền mạch', FILM_AUTHOR_PROMPTS: 'dựng prompt', FILM_GEN_EVENTS: 'tạo clip cả plan', FILM_EVENT_STRUCTURE: 'đổi cấu trúc video',
  FILM_WORKFLOW_RUN: 'máy tự chạy chặng',
};
export const JOBS_UNKNOWN_TEXT = 'Chưa kiểm được việc nền đang chạy — bấm Tải lại rồi thử lại.';

/**
 * Có việc nền nào đang ghi plan khiến KHÔNG nên sửa tay video `eventId`?
 * (1) bất kỳ việc nào trong `PLAN_WRITE_JOB_KINDS` trên plan; (2) việc tạo clip (`FILM_GEN_CLIP` / `FILM_REGEN_CLIP`) của CHÍNH video này — clip xong thì `settleEventClipDone` gỡ cờ `clip`, sửa giữa chừng sẽ bị báo `READY` sai.
 * Việc tạo clip của video KHÁC không chặn. Cùng cách khớp với `videoBusyOf` (theo `eventId`, hoặc khoá = eventId khi job không có `eventId`).
 */
/** Các loại việc nền GHI plan đang chạy (đã khử trùng, theo `PLAN_WRITE_JOB_KINDS`). Dùng chung cho `planWriteBlock` (sửa tay) và hàng "Máy tự chạy" (P2c-2). */
export function planWriteKindsOf(jobs: Array<Pick<FilmRunningJob, 'type'>>): Array<(typeof PLAN_WRITE_JOB_KINDS)[number]> {
  return Array.from(new Set(jobs.map((j) => j.type).filter((k): k is (typeof PLAN_WRITE_JOB_KINDS)[number] => (PLAN_WRITE_JOB_KINDS as readonly string[]).includes(k))));
}
/** Nhãn tiếng Việt của một loại việc ghi plan; loại lạ ⇒ bỏ tiền tố `FILM_`. */
export const planWriteLabelOf = (type: string): string => (PLAN_WRITE_JOB_LABEL as Record<string, string>)[type] ?? type.replace(/^FILM_/, '');

export function planWriteBlock(jobs: Array<Pick<FilmRunningJob, 'type' | 'eventId' | 'key'>>, eventId: string): { blocked: boolean; reason?: string } {
  const kinds = planWriteKindsOf(jobs);
  if (kinds.length) return { blocked: true, reason: `Đang chạy lượt AI trên cả plan (${kinds.map((k) => PLAN_WRITE_JOB_LABEL[k]).join(', ')}) — đợi xong rồi sửa.` };
  const clipOfThisVideo = jobs.some((j) => (j.type === 'FILM_GEN_CLIP' || j.type === 'FILM_REGEN_CLIP') && !!eventId && (j.eventId === eventId || (!j.eventId && j.key === eventId)));
  if (clipOfThisVideo) return { blocked: true, reason: 'Video này đang tạo clip ở nền — đợi xong rồi sửa.' };
  return { blocked: false };
}

/** Ba thao tác của trình sửa tay, dùng chung cho Kịch bản (`SCRIPT_*`), Đạo diễn (`DIRECTING_*`) và Quay phim (`CINEMATOGRAPHY_*`). Không xác nhận (lưu cả khi video đã có clip, sau đó hiện dòng "đã cũ"). */
function editActions(prefix: 'SCRIPT' | 'DIRECTING' | 'CINEMATOGRAPHY', ctx: WorkflowActionCtx): ActionState[] {
  const e = ctx.edit ?? { eventId: '', editing: false, dirty: false, jobs: [] };
  const block = planWriteBlock(e.jobs, e.eventId);
  return [
    act(`${prefix}_EDIT`, 'Sửa', { visible: true, enabled: !block.blocked, reason: block.reason }),
    act(`${prefix}_SAVE`, 'Lưu', { visible: e.editing, enabled: e.dirty && !block.blocked, reason: block.blocked ? block.reason : 'Chưa có thay đổi để lưu.' }),
    act(`${prefix}_DISCARD`, 'Huỷ', { visible: e.editing, enabled: true }),
  ];
}

export function actionsFor(step: WorkflowStepKey, ctx: WorkflowActionCtx): ActionState[] {
  if (!ctx.isSky) return [];
  if (step === 'SCRIPT') return editActions('SCRIPT', ctx);
  if (step === 'DIRECTING') return editActions('DIRECTING', ctx);
  if (step === 'CINEMATOGRAPHY') return editActions('CINEMATOGRAPHY', ctx);
  if (step === 'MAP') return mapActions(ctx);
  if (step === 'STORYBOARD') return storyboardActions(ctx);
  if (step === 'PROMPT') return promptActions(ctx);
  if (step === 'VIDEO') return videoActions(ctx);
  if (step === 'ASSEMBLY') return assemblyActions(ctx);
  return [];
}

// ───────────────────────── hộp xác nhận (KHÔNG giá) ─────────────────────────

export interface ConfirmContent { title: string; body: string; okText: string; cancelText: string }
const GEN_BODY = 'Lượt này gọi dịch vụ tạo ảnh thật và có thể mất vài phút. Kết quả sẽ hiện ở bước này và trong Lịch sử khi xong.';

export function confirmContent(kind: Exclude<ConfirmKind, 'NONE'>, scope: 'MAP' | 'SB' | 'PROMPT' | 'VIDEO' | 'ASSEMBLY'): ConfirmContent {
  switch (kind) {
    case 'GEN_IMAGE': return { title: scope === 'MAP' ? 'Tạo ảnh sơ đồ?' : 'Tạo tấm storyboard?', body: GEN_BODY, okText: 'Tạo ảnh', cancelText: 'Huỷ' };
    case 'OVERWRITE_EDITED': return { title: 'Thay bản bạn đã sửa tay?', body: 'Bản bạn đã sửa tay sẽ bị thay bằng bản AI dựng, không hoàn tác được.', okText: 'Thay bằng bản AI', cancelText: 'Huỷ' };
    // Bản đủ chi tiết (engine / giây / số bản …) ở `videoConfirmContent` / `assemblyConfirmContent`; đây là bản chung khi caller chưa có ctx.
    case 'GEN_VIDEO': return { title: 'Tạo clip?', body: VIDEO_GEN_BODY, okText: 'Tạo clip', cancelText: 'Huỷ' };
    case 'GEN_ASSEMBLY': return { title: 'Dựng cả tập?', body: ASSEMBLY_BODY, okText: 'Dựng', cancelText: 'Huỷ' };
    case 'DELETE':
      return scope === 'VIDEO' ? clipDeleteConfirmContent(false)
        : { title: 'Xoá storyboard?', body: 'Khung và tấm ảnh của video này sẽ bị xoá khỏi video (ảnh đã tạo vẫn nằm trong kho lưu trữ).', okText: 'Xoá', cancelText: 'Huỷ' };
  }
}
/**
 * Cổng xác nhận của «Dựng lại prompt» / «Tạo lại prompt theo kiểu mới»: prompt đã SỬA TAY (EDITED) ⇒ LUÔN hỏi (thay thế = phá huỷ, không phụ thuộc U9) và chỉ gửi `force` sau khi xác nhận;
 * prompt thường ⇒ chạy ngay, không hộp.
 */
export function promptRebuildGateOf(a: Pick<ActionState, 'confirm' | 'payload'> | undefined): { confirm: ConfirmContent | null; payload: Record<string, unknown> } {
  const confirm = a && a.confirm === 'OVERWRITE_EDITED' ? confirmContent('OVERWRITE_EDITED', 'PROMPT') : null;
  return { confirm, payload: (a?.payload as Record<string, unknown> | undefined) ?? {} };
}
const VIDEO_GEN_BODY = 'Lượt này gọi dịch vụ tạo video thật và mất vài phút; kết quả hiện ở danh sách bản clip.';
const ASSEMBLY_BODY = 'Ghép clip đã chọn của các video, kèm phụ đề và nhạc nền (nếu có). Chạy ở nền, mất vài phút.';

const VIDEO_TITLES: Record<string, string> = { VIDEO_GEN: 'Tạo clip?', VIDEO_VARIANT: 'Thêm bản clip?' };
/** Hộp xác nhận gen clip: nêu ENGINE + độ phân giải + SỐ GIÂY + SỐ BẢN (không giá) — một clip 2.5 / 27 giây đắt gấp ~10 lần một clip mini 10 giây, người bấm phải biết mình gọi loại nào. */
export function videoConfirmContent(actionKey: string, v: NonNullable<WorkflowActionCtx['video']>): ConfirmContent {
  const spec = [v.engineLabel, v.resolution, v.durationS != null ? `${v.durationS} giây` : 'số giây theo video', `${v.count} bản`].filter(Boolean).join(' · ');
  const stale = v.promptStale ? ' Prompt đã cũ so với nội dung — nên dựng lại ở bước 7 trước.' : '';
  return { title: VIDEO_TITLES[actionKey] ?? 'Tạo clip?', body: `${spec}. ${VIDEO_GEN_BODY}${stale}`, okText: 'Tạo clip', cancelText: 'Huỷ' };
}
export function assemblyConfirmContent(a: NonNullable<WorkflowActionCtx['assembly']>): ConfirmContent {
  return { title: 'Dựng cả tập?', body: `Ghép clip đã chọn của ${a.total} video, kèm phụ đề và nhạc nền (nếu có). Chạy ở nền, mất vài phút.`, okText: 'Dựng', cancelText: 'Huỷ' };
}
export function clipDeleteConfirmContent(isSelected: boolean): ConfirmContent {
  return { title: 'Xoá bản clip này?', body: `Bản clip sẽ bị xoá khỏi video.${isSelected ? ' Đây là bản đang chọn — video sẽ trống.' : ''}`, okText: 'Xoá', cancelText: 'Huỷ' };
}
export const confirmTextOf = (c: ConfirmContent): string => `${c.title} ${c.body} ${c.okText} ${c.cancelText}`;

// ───────────────────────── lỗi API ⇒ câu tiếng Việt ─────────────────────────

export type ActionErrorKind = 'CHANGED_ELSEWHERE' | 'CHANGED' | 'STALE' | 'NO_IMAGE' | 'BUSY' | 'EDITED' | 'NOT_FOUND' | 'PROMPT' | 'ENGINE' | 'CLIP_CONFIG' | 'STAGE1_INCOMPLETE' | 'SCRIPT_GATE' | 'NOT_SKY' | 'CANONICAL' | 'DIRECTION' | 'OTHER';
export interface ActionError { kind: ActionErrorKind; text: string; /** luôn true: sau mọi lỗi phải tải lại cả widget và trạng thái */ reload: true; /** true ⇒ lệnh CHƯA chạy gì và gửi lại một lần là hợp lệ (xem trước đã lệch): băng lỗi hiện nút «Thử lại». */ retry?: true }

export const CHANGED_ELSEWHERE_TEXT = 'Video này vừa đổi ở nơi khác — tải lại rồi sửa lại.';
/** Câu lỗi nghiệp vụ cho khối bước. Mã thô không bao giờ lộ ra chữ; mã lạ rơi về câu server (`apiErrorText`). */
export function workflowActionError(err: any, what: string): ActionError {
  const d = err?.response?.data;
  const code = typeof d?.code === 'string' ? d.code : typeof d?.errorCode === 'string' ? d.errorCode : ''; // `{errorCode, messages[]}` của restInterceptor
  const mk = (kind: ActionErrorKind, text: string): ActionError => ({ kind, text: `${what}: ${text}`, reload: true });
  // 261010 P2b-3 — không phải mã server: FE tự phát hiện (dấu vân tay) rằng video đã đổi ở nơi khác (vd. UI cũ vừa Lưu) nên KHÔNG gọi `batch-edit`.
  if (err?.code === 'CHANGED_ELSEWHERE' || code === 'CHANGED_ELSEWHERE') return { kind: 'CHANGED_ELSEWHERE', text: CHANGED_ELSEWHERE_TEXT, reload: true };
  // 261010 P9 — xem trước (hash xác nhận / ảnh chụp tài sản) lệch lúc gửi: BE từ chối TRƯỚC khi ghi gì ⇒ nói rõ «chưa chạy gì» + cho thử lại. Mã thô không lộ.
  if (code === 'CONFIRMATION_CHANGED') return { ...mk('CHANGED', 'danh sách việc máy sắp làm vừa đổi so với lúc bạn xem — chưa chạy gì và chưa duyệt gì. Đã tải lại, bấm «Thử lại» để xem lại rồi gửi.'), retry: true };
  if (code === 'PREVIEW_CHANGED') return { ...mk('CHANGED', 'danh sách tài sản vừa đổi so với lúc bạn xem — chưa chạy gì và chưa duyệt gì. Đã tải lại, bấm «Thử lại» để xem lại rồi gửi.'), retry: true };
  if (code === 'HASH_CHANGED') return mk('CHANGED', 'dữ liệu chấm vừa đổi sau khi bạn xem — đã tải lại, hãy xem lại rồi làm lại.');
  if (code === 'UPSTREAM_REQUIRED') return mk('OTHER', 'mục này cần kết quả bước trước còn sẵn sàng — hoàn tất hoặc duyệt bước trước rồi làm lại.');
  if (code === 'SKIP_CONFIRMATION_REQUIRED') return mk('CHANGED', 'cần xác nhận lại các mục bỏ qua trước khi duyệt — đã tải lại, hãy xem lại rồi duyệt lại.');
  if (code === 'FRAMES_CHANGED') return mk('CHANGED', 'khung đã đổi ở nơi khác (hoặc AI vừa ghi) — đã tải lại bản mới; chỉnh sửa chưa lưu của bạn bị bỏ, hãy xem lại rồi làm lại.');
  if (code === 'LINKS_CHANGED') return mk('CHANGED', 'liên kết đã đổi ở nơi khác (hoặc AI vừa ghi) — đã tải lại, hãy xem lại rồi làm lại.');
  if (code === 'STALE') return mk('STALE', 'ảnh đã cũ so với đầu vào hiện tại — tạo lại ảnh rồi mới duyệt.');
  if (code === 'NO_IMAGE') return mk('NO_IMAGE', 'chưa có ảnh — tạo ảnh trước khi duyệt.');
  if (code === 'STORYBOARD_EDITED') return mk('EDITED', 'storyboard này đã có bản sửa tay (hoặc agent sửa) — cần xác nhận thay thế; đã tải lại, hãy bấm soạn lại để xác nhận.');
  if (code === 'NOT_FOUND') return mk('NOT_FOUND', 'không tìm thấy — có thể đã bị xoá ở nơi khác; đã tải lại.');
  // P2b-2 — gen / chọn clip. Mã THẬT của BE: cổng prompt trả `PROMPT_MODE_MISMATCH` | `NO_PROMPT` (doc ghi `PROMPT_MISSING`: nhận cả hai). Đứng TRƯỚC nhánh 409-bận.
  if (code === 'PROMPT_MODE_MISMATCH') return mk('PROMPT', 'prompt đang lưu lệch kiểu hiện hành của video — dựng lại prompt ở bước 7 rồi tạo clip lại.');
  if (code === 'NO_PROMPT' || code === 'PROMPT_MISSING') return mk('PROMPT', 'video chưa có prompt — dựng prompt ở bước 7 trước.');
  if (code === 'ENGINE_MODEL_MISMATCH') return mk('ENGINE', 'model đã chọn không hợp với engine của video — đã tải lại, hãy chọn lại model.');
  if (code === 'CLIP_CONFIG_MISMATCH') return mk('CLIP_CONFIG', 'clip này gen theo engine/giây khác video hiện tại nên không chọn được — hãy tạo clip mới.');
  // 261010 P4b-2 — tab "Bối cảnh" (ảnh chuẩn / ảnh hướng). Đứng TRƯỚC nhánh 409-bận chung và fallback; mã thô không lộ ra chữ. `STALE` / `NO_IMAGE` (duyệt view cũ / chưa ảnh) dùng nhánh đã có ở trên.
  if (code === 'CANONICAL_NOT_APPROVED') return mk('CANONICAL', 'ảnh chuẩn đang chờ cập nhật trạng thái — tải lại hoặc duyệt bước Tài sản.');
  if (code === 'CANONICAL_MISSING') return mk('CANONICAL', 'bối cảnh chưa có ảnh chuẩn — tạo ảnh chuẩn trên thẻ bối cảnh (bước 2 › Bối cảnh) trước.');
  if (code === 'DIRECTION_EXISTS') return mk('DIRECTION', 'mã hướng này đã có ở bối cảnh này — đặt mã khác.');
  if (code === 'BAD_FIELDS') return mk('DIRECTION', 'thiếu nhãn / mô tả, hoặc mã hướng sai dạng (chữ thường, số, gạch nối).');
  // `JOB_RUNNING` của ảnh hướng (PATCH / generate): BE trả `details.jobKey = "view:<id>"`, KHÔNG có `jobType` ⇒ nhánh plan bên dưới sẽ in `việc ""`.
  if (code === 'JOB_RUNNING' && !d?.jobType && String(d?.details?.jobKey ?? '').startsWith('view:')) return mk('BUSY', 'ảnh hướng này đang được tạo ở nền — đợi xong rồi bấm Tải lại, đừng bấm lại.');
  // 261010 P2c-2 — nút "Chạy chặng" + khoá ngược. Đứng TRƯỚC nhánh 409-bận chung (JOB_RUNNING mang `busy: true`).
  if (code === 'WORKFLOW_RUN_ACTIVE') return mk('BUSY', 'máy tự chạy chặng đang chạy trên plan này — đợi xong rồi thao tác.');
  // 261010 P8a — cổng "Duyệt kịch bản". Đứng TRƯỚC nhánh 409-bận chung. Mã thô không lộ ra chữ; câu nào cũng dẫn tới hành động tiếp theo.
  if (code === 'SCRIPT_NOT_APPROVED') return mk('SCRIPT_GATE', 'chưa duyệt kịch bản — bấm "Duyệt và chạy tiếp" ở hàng Máy tự chạy (nếu chưa thấy nút đó, tải lại trang).');
  if (code === 'SCRIPT_APPROVAL_OUTDATED') return mk('SCRIPT_GATE', 'kịch bản đã đổi sau lần duyệt gần nhất — bấm "Duyệt lại và chạy tiếp" ở hàng Máy tự chạy.');
  if (code === 'SCRIPT_CHANGED') return mk('SCRIPT_GATE', 'kịch bản vừa đổi — xem lại rồi duyệt lại (đã tải lại bản mới nhất).');
  if (code === 'NO_ACTIVE_APPROVAL') return mk('SCRIPT_GATE', 'kịch bản chưa được duyệt (hoặc đã huỷ duyệt) — không có gì để huỷ.');
  // Duyệt ĐÃ ghi nhưng chưa mở được chặng 2 (race bận): duyệt vẫn còn ⇒ chỉ cần bấm "Chạy chặng 2" sau, không phải duyệt lại.
  if (code === 'JOB_RUNNING' && d?.approved === true) return mk('BUSY', 'Đã duyệt kịch bản, nhưng chưa mở được chặng 2 vì plan đang bận — đợi việc đang chạy xong rồi bấm "Chạy chặng 2".');
  if (code === 'JOB_RUNNING') return mk('BUSY', `plan đang có việc ${d?.jobType ? `"${planWriteLabelOf(String(d.jobType))}" ` : ''}chạy ở nền — đợi xong rồi bấm Tải lại, đừng bấm lại.`);
  if (code === 'STAGE1_INCOMPLETE') {
    const det = d?.details ?? {};
    const miss = Array.isArray(det.missingDurationEvents) ? det.missingDurationEvents.length : 0;
    const why = [det.noDialogue ? 'plan chưa có thoại nào' : '', miss ? `${miss} video chưa có số giây` : ''].filter(Boolean).join('; ');
    return mk('STAGE1_INCOMPLETE', `chặng 1 chưa xong${why ? ` — ${why}` : ''}. Chạy chặng 1 trước.`);
  }
  if (code === 'NOT_SKY_PLAN') return mk('NOT_SKY', 'plan này không dùng luồng mới — máy tự chạy chặng chỉ dùng cho plan dùng luồng mới; làm ở UI cũ.');
  if (err?.response?.status === 409 && d?.busy) {
    const m = typeof d.message === 'string' && d.message ? ` (${d.message})` : '';
    return mk('BUSY', `việc này đang chạy ở nền${m} — đợi xong rồi bấm Tải lại, đừng bấm lại.`);
  }
  return { kind: 'OTHER', text: apiErrorText(err, what), reload: true };
}

// ───────────────────────── dựng ctx từ dữ liệu FE ─────────────────────────

/** Loại job đang chạy liên quan video này: job của video + job cấp plan (không `eventId`). */
export const runningKindsOf = (jobs: Array<Pick<FilmRunningJob, 'type' | 'eventId'>>, eventId: string): string[] =>
  Array.from(new Set(jobs.filter((j) => !j.eventId || j.eventId === eventId).map((j) => j.type)));

export function mapCtxOf(
  resp: Pick<PositionMapsResponse, 'maps' | 'links'> | null | undefined, eventId: string, phaseOrders: number[], jobs: Array<Pick<FilmRunningJob, 'type' | 'key'>>,
): NonNullable<WorkflowActionCtx['map']> {
  const stages: Array<number | null> = phaseOrders.length ? phaseOrders : [null];
  if (!resp) return { setups: [], linkedPhases: 0, totalPhases: stages.length };
  const byId = new Map(resp.maps.map((m) => [m.id, m]));
  const links = resp.links.valid.filter((l) => l.eventId === eventId);
  const linkedPhases = stages.filter((s) => links.some((l) => (l.phaseOrder ?? null) === s && byId.has(l.positionMapId))).length;
  const ids = Array.from(new Set(links.map((l) => l.positionMapId))).filter((id) => byId.has(id));
  const busyIds = new Set(jobs.filter((j) => j.type === JOB_KIND.MAP).map((j) => j.key));
  const setups = ids.map((id) => {
    const m = byId.get(id)!;
    const isAnchor = m.role === 'ANCHOR';
    const anchor = m.anchorMapId ? byId.get(m.anchorMapId) : undefined;
    const approval = m.approval?.state;
    return {
      id, hasImage: !!m.imageUrl, approvalState: (approval === 'APPROVED' || approval === 'OUTDATED' ? approval : 'NONE') as ApprovalStateLite,
      isAnchor, anchorHasImage: isAnchor ? true : !!anchor?.imageUrl,
      stale: !!m.imageUrl && (m.staleness === 'INPUT_CHANGED' || m.staleness === 'ANCHOR_REGENERATED' || approval === 'OUTDATED'),
      busy: busyIds.has(id),
    };
  });
  return { setups, linkedPhases, totalPhases: stages.length };
}

export function storyboardCtxOf(sb: Storyboard | undefined | null, ev: Pick<FilmEvent, 'shots'>, isDirty: boolean): NonNullable<WorkflowActionCtx['storyboard']> {
  const state = sb?.approval?.state;
  const author = sb?.authorStatus as string | undefined;
  return {
    hasShots: (ev.shots || []).length > 0, hasFrames: (sb?.frames?.length ?? 0) > 0, isDirty,
    hasImage: !!sb?.sheetRawUrl, // ảnh được duyệt = ảnh gốc không chữ
    staleness: sb?.staleness ?? null,
    approvalState: state === 'APPROVED' || state === 'OUTDATED' ? state : 'NONE',
    authorStatus: !sb ? null : author === 'EDITED' ? 'EDITED' : author === 'AGENT' ? 'AGENT' : 'AI',
  };
}

export function promptCtxOf(ev: Pick<FilmEvent, 'authorPrompt' | 'authorStatus' | 'promptModeState'>): NonNullable<WorkflowActionCtx['prompt']> {
  const hasPrompt = !!String(ev.authorPrompt ?? '').trim();
  const status = ev.authorStatus === 'AUTHORED' || ev.authorStatus === 'EDITED' ? ev.authorStatus : null;
  return { hasPrompt, authorStatus: status, promptModeState: ev.promptModeState ?? (hasPrompt ? 'OK' : 'MISSING') };
}

/** Job có ở lượt poll trước mà mất ở lượt này ⇒ vừa xong ⇒ tải lại dữ liệu chi tiết (storyboard/sơ đồ…). Lần đầu (prev null) ⇒ không có gì. */
export function jobsFinished<T extends { id: string }>(prev: T[] | null, now: Array<{ id: string }>): T[] {
  if (!prev) return [];
  const alive = new Set(now.map((j) => j.id));
  return prev.filter((j) => !alive.has(j.id));
}

// ───────────────────────── widget cũ: ẩn giá + chặn theo chính sách (prop tuỳ chọn; không truyền ⇒ UI cũ y nguyên) ─────────────────────────

/** Nhãn nút gen. Không `hideCost` ⇒ ĐÚNG chuỗi cũ ("Gen ảnh · ~$0,10"); `hideCost` ⇒ không có giá ("Tạo ảnh"). */
export function genButtonLabel(o: { hasImage: boolean; noun: 'ảnh' | 'tấm'; cost: string; hideCost?: boolean }): string {
  if (o.hideCost) return `${o.hasImage ? 'Tạo lại' : 'Tạo'} ${o.noun}`;
  return `${o.hasImage ? 'Gen lại' : `Gen ${o.noun}`} · ${o.cost}`;
}
/** Dòng "đã tốn …" của lần gen gần nhất: `hideCost` ⇒ ẩn. */
export const spentLabel = (spent: string | null | undefined, hideCost?: boolean): string | null => (hideCost ? null : (spent ?? null));

export type ActionGate = Pick<ActionState, 'visible' | 'enabled' | 'reason'>;
/** Ghép cổng riêng của widget với cổng của chính sách: chính sách chỉ THÊM ràng buộc (ẩn / tắt), không bao giờ mở thứ widget đã khoá. Không có chính sách ⇒ y như cũ. */
export function combineGate(own: { ok: boolean; reason?: string }, policy?: ActionGate): { visible: boolean; ok: boolean; reason?: string } {
  if (!policy) return { visible: true, ok: own.ok, reason: own.reason };
  if (!own.ok) return { visible: policy.visible, ok: false, reason: own.reason };
  return { visible: policy.visible, ok: policy.enabled, reason: policy.enabled ? undefined : policy.reason };
}


// ───────────────────────── P2b-2: dựng ctx Video / Ghép từ dữ liệu FE ─────────────────────────

export const VIDEO_JOB_KIND = { GEN_CLIP: 'FILM_GEN_CLIP', REGEN_CLIP: 'FILM_REGEN_CLIP', GEN_EVENTS: 'FILM_GEN_EVENTS', ASSEMBLE: 'FILM_ASSEMBLE' } as const;

/** Nhãn engine cho hộp xác nhận. Video chạy 2.5 ⇒ luôn "Seedance 2.5" (model chọn tay bị BE bỏ qua / từ chối); còn lại theo model đã chọn, mặc định mini — cùng luật `effectiveTuningLabel`. */
export function engineLabelOf(videoEngine: string | null | undefined, model: string | null | undefined): string {
  if (videoEngine === 'SEEDANCE_25') return 'Seedance 2.5';
  const m = model || 'mini';
  return m === '2.5' ? 'Seedance 2.5' : `Seedance 2.0 ${m}`;
}

/** BẢN SAO BE `selectedClipUsable` (utils/filmEngineGen.ts) cho plan Sky: video chưa có engine ⇒ dùng được; có engine ⇒ clip phải cùng engine (`meta.videoEngine`) + cùng số giây. Sửa một bên thì sửa cả hai. */
export function clipUsableFor(ev: { videoEngine?: string | null; genDurationS?: number | null }, clip: { durationS?: number | null; meta?: unknown }): boolean {
  const engine = ev.videoEngine ?? null;
  if (!engine) return true;
  const meta = clip.meta && typeof clip.meta === 'object' ? (clip.meta as { videoEngine?: string | null }) : {};
  return (meta.videoEngine ?? null) === engine && clip.durationS === ev.genDurationS;
}

/** Video đang gen ở nền: job gen/regen của đúng video (theo `eventId` hoặc khoá = eventId), gen cả plan khi video CHƯA có clip được chọn (khớp luật BE), hoặc có clip PENDING. KHÔNG tính job ghép / job prompt. */
export function videoBusyOf(
  jobs: Array<Pick<FilmRunningJob, 'type' | 'eventId' | 'key'>>,
  ev: { id: string; selectedClipId?: string | null; clips?: Array<{ status?: string | null }> | null },
): boolean {
  const byJob = jobs.some((j) => (
    ((j.type === VIDEO_JOB_KIND.GEN_CLIP || j.type === VIDEO_JOB_KIND.REGEN_CLIP) && (j.eventId === ev.id || (!j.eventId && j.key === ev.id)))
    || (j.type === VIDEO_JOB_KIND.GEN_EVENTS && !ev.selectedClipId)
  ));
  return byJob || (ev.clips || []).some((c) => c.status === 'PENDING');
}

/**
 * Tuỳ chọn gen THỰC SỰ gửi lên `POST /events/:id/gen` cho khung mới — nguồn DUY NHẤT cho cả payload lẫn chuỗi hộp xác nhận (`videoCtxOf` đọc từ đây), nên hai thứ không thể lệch nhau.
 * Luôn gửi `resolution` TƯỜNG MINH (mặc định 480p; video 2.5 ⇒ 480p như đang hiển thị): không gửi thì BE rơi về env `SEEDANCE_RESOLUTION` (dev đang 720p) ⇒ clip ra khác điều hộp nói.
 * `model` cũng tường minh với video 2.0 (mặc định mini; không thì BE rơi về env `SEEDANCE_MODEL`); video 2.5 KHÔNG gửi model (BE tự khoá 2.5, gửi chéo họ ⇒ 400). Chỉ `model | resolution | count`: không có `trialRun`.
 */
export function genTuningPayload(ev: { videoEngine?: string | null; generation?: { engine?: string; model?: string | null; resolution?: string | null } | null }, tuning: GenTuning): { model?: NonNullable<GenTuning['model']>; resolution: NonNullable<GenTuning['resolution']>; count: number } {
  // 261010 UI2 (vá #3): video của plan opt-in ⇒ cấu hình ĐÃ LƯU là nguồn duy nhất (BE 409 nếu lệch); chỉ số bản theo lượt.
  const saved = optInTuningOf(ev);
  if (saved) return { ...(saved.model ? { model: saved.model } : {}), resolution: saved.resolution, count: tuning.count || 1 };
  const is25 = ev.videoEngine === 'SEEDANCE_25';
  return {
    ...(is25 ? {} : { model: tuning.model || 'mini' }),
    resolution: is25 ? '480p' : (tuning.resolution || '480p'),
    count: tuning.count || 1,
  };
}

export function videoCtxOf(
  ev: Pick<FilmEvent, 'id' | 'videoEngine' | 'generation' | 'genDurationS' | 'selectedClipId' | 'staleStages' | 'authorPrompt' | 'promptModeState' | 'clips'>,
  tuning: GenTuning,
  jobs: Array<Pick<FilmRunningJob, 'type' | 'eventId' | 'key'>>,
): NonNullable<WorkflowActionCtx['video']> {
  const clips = ev.clips || [];
  const sent = genTuningPayload(ev, tuning); // hộp xác nhận đọc đúng thứ sẽ gửi
  const hasPrompt = !!String(ev.authorPrompt ?? '').trim();
  const state = ev.promptModeState ?? (hasPrompt ? 'OK' : 'MISSING');
  const promptGate = state === 'OK' ? { ok: true }
    : state === 'MISMATCH' ? { ok: false, reason: 'Prompt lệch kiểu hiện hành — dựng lại prompt ở bước 7.' }
    : { ok: false, reason: 'Chưa có prompt — dựng prompt ở bước 7 trước.' };
  return {
    engineLabel: engineLabelOf(ev.videoEngine, sent.model),
    resolution: sent.resolution,
    durationS: typeof ev.genDurationS === 'number' ? ev.genDurationS : null,
    count: sent.count,
    hasClips: clips.length > 0,
    selectedClipId: ev.selectedClipId ?? null,
    clips: clips.map((c) => ({ id: c.id, status: (c.status ?? 'PENDING') as 'PENDING' | 'DONE' | 'FAILED', usable: clipUsableFor(ev, c) })),
    promptGate,
    promptStale: (ev.staleStages || []).includes('prompt'),
    busy: videoBusyOf(jobs, ev),
  };
}

/**
 * Ctx Ghép. `events` = danh sách video của plan (từ `workflow-status`, đã sắp); `detailEvents` = chi tiết plan (clip đã chọn). Khớp gap-check của server:
 * một video "đủ" khi có clip được chọn VÀ clip đó có `videoUrl`. Video thiếu trong chi tiết ⇒ tính là thiếu.
 */
export function assemblyCtxOf(
  events: Array<{ eventId: string; eventOrder: number }>,
  detailEvents: Array<Pick<FilmEvent, 'id' | 'selectedClipId' | 'clips'>> | null | undefined,
  exp: { exportStatus: FilmExportStatus | null | undefined; exportUrl: string | null | undefined; exportStale: boolean | null | undefined },
  jobs: Array<Pick<FilmRunningJob, 'type'>>,
): NonNullable<WorkflowActionCtx['assembly']> {
  const byId = new Map((detailEvents || []).map((e) => [e.id, e]));
  const hasSelected = (id: string): boolean => {
    const e = byId.get(id);
    const sel = e?.selectedClipId ? (e.clips || []).find((c) => c.id === e.selectedClipId) : undefined;
    return !!sel && !!sel.videoUrl;
  };
  const missing = events.filter((e) => !hasSelected(e.eventId)).map((e) => ({ eventId: e.eventId, eventOrder: e.eventOrder }));
  return {
    total: events.length, withSelected: events.length - missing.length, missing,
    exportStatus: exp.exportStatus ?? null, hasExport: !!exp.exportUrl, exportStale: !!exp.exportStale,
    busy: exp.exportStatus === 'rendering' || jobs.some((j) => j.type === VIDEO_JOB_KIND.ASSEMBLE),
  };
}
